import React from 'react';
import { styled, theme } from '../stitches';

type CountdownVariant =
  | 'landingPage'
  | 'release'
  | 'feedReleaseDropped'
  | 'minting'
  | 'landingPageHeroCleanup';

const HeroCountdownNumber = ({
  variant,
  value,
  label,
  isLast,
  fuschia,
}: {
  variant: CountdownVariant;
  value: number;
  label: string;
  fuschia?: boolean;
  isLast?: boolean;
}) => {
  return (
    <>
      <HeroCountdownBlock variant={variant} fuschia={fuschia}>
        <p suppressHydrationWarning>{String(value).padStart(2, '0')}</p>
        <span>{label}</span>
      </HeroCountdownBlock>
      {!isLast && (
        <Colon variant={variant} fuschia={fuschia}>
          :
        </Colon>
      )}
    </>
  );
};

// Missing Test
// TODO: https://linear.app/sound/issue/ENG-2492/add-tests-for-listeningpartycountdown
export const ListeningPartyCountdown = ({
  seconds,
  minutes,
  hours,
  days,
  fuschia,
  variant = 'release',
  small,
}: {
  seconds: number;
  minutes: number;
  hours: number;
  days: number;
  fuschia?: boolean;
  variant?: CountdownVariant;
  small?: boolean;
}) => {
  return (
    <FullCountdownContainer variant={variant} fuschia={fuschia} small={small}>
      <HeroCountdownNumber variant={variant} fuschia={fuschia} value={days} label="days" />
      <HeroCountdownNumber variant={variant} fuschia={fuschia} value={hours} label="hrs" />
      <HeroCountdownNumber variant={variant} fuschia={fuschia} value={minutes} label="min" />
      <HeroCountdownNumber variant={variant} fuschia={fuschia} value={seconds} label="sec" isLast />
    </FullCountdownContainer>
  );
};

const containerStyles = {
  background: 'rgba(255, 255, 255, 0.7)',
  boxShadow: 'inset 2px 2px 24px rgba(255, 255, 255, 0.6)',
  backdropFilter: 'blur(40px) saturate(4)',
  borderRadius: '8px',
};

const FullCountdownContainer = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  margin: '0 auto',
  maxWidth: '264px',
  paddingRight: '12px',
  '@tablet': {
    padding: 0,
    boxShadow: 'none',
    backdropFilter: 'none',
    borderRadius: '0',
    maxWidth: '337px',
  },

  p: {
    paddingTop: 0,
    height: 'fit-content',
  },
  variants: {
    fuschia: {
      true: {
        color: theme.gradients.fuchsia,
      },
    },
    variant: {
      feedReleaseDropped: {
        color: '$white',
      },
      minting: {},
      landingPage: {
        ...containerStyles,
        padding: '10px 12px 10px 10px',
        '@tablet': {
          background: '#fff',
          margin: 'auto',
          marginTop: 24,
          padding: 0,
        },
        '@laptop': {
          marginLeft: 0,
        },
        p: {
          numerals: 'S',
        },
      },
      release: {
        color: '$white',
        '@tablet': {
          marginBottom: 34,
          marginTop: 4,
        },
      },
      landingPageHeroCleanup: {
        marginTop: 24,
        marginBottom: 0,
      },
    },
    small: {
      true: {
        paddingRight: 0,
        width: '100%',
        span: {
          display: 'none',
        },
        p: {
          numerals: 'XXS',
          color: 'white',
        },
      },
    },
  },
});

const HeroCountdownBlock = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 16,
  rowGap: 6,

  span: {
    base: 'XS',
    textTransform: 'uppercase',
    fontWeight: 600,
  },
  p: {
    numerals: 'S',
  },
  variants: {
    fuschia: {
      true: {
        background: theme.gradients.fuchsia,
        '-webkit-background-clip': 'text',
        '-webkit-text-fill-color': 'transparent',
        backgroundClip: 'text',
        textFillColor: 'transparent',
        borderRadius: 0,
      },
    },
    variant: {
      minting: {},
      feedReleaseDropped: {
        p: {
          numerals: 'XXS',
          color: '$white',
        },
        span: {
          color: '$white',
        },
      },
      landingPage: {},
      release: {
        p: {
          color: '$white',
        },
        span: {
          color: '$white',
        },
      },
      landingPageHeroCleanup: {
        p: {
          color: '$white',
        },
        span: {
          color: '$white',
        },
      },
    },
  },
});

const Colon = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  numerals: 'S',
  variants: {
    fuschia: {
      true: {
        background: theme.gradients.fuchsia,
        '-webkit-background-clip': 'text',
        '-webkit-text-fill-color': 'transparent',
        backgroundClip: 'text',
        textFillColor: 'transparent',
      },
    },
    variant: {
      minting: {},
      feedReleaseDropped: {
        numerals: 'XXS',
        color: '$white',
      },
      release: {
        color: '$white',
      },
      landingPage: {
        color: '$neutral800',
      },
      landingPageHeroCleanup: {
        color: '$white',
      },
    },
  },
});
